import { useState } from 'react';
import Container from '@cloudscape-design/components/container';
import SpaceBetween from '@cloudscape-design/components/space-between';
import ExpandableSection from '@cloudscape-design/components/expandable-section';
import Box from '@cloudscape-design/components/box';
import Icon from '@cloudscape-design/components/icon';
import { formatRelativeTime } from '../utils/dateUtils';

interface ThreadMessage {
  messageId: string;
  from: string;
  subject: string;
  receivedAt: string;
  readStatus: string;
  body: string | null;
  snippet?: string;
}

interface ThreadViewProps {
  /** All messages in the same thread, sorted chronologically (oldest first) */
  messages: ThreadMessage[];
  /** The currently selected/focused message ID */
  activeMessageId: string;
}

/**
 * WM-8: Thread view — groups messages by threadId and displays conversation
 * in chronological order. The active message is expanded; others are collapsed.
 */
export default function ThreadView({ messages, activeMessageId }: ThreadViewProps) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set([activeMessageId]));

  if (messages.length <= 1) {
    // Single message — no thread, just render body directly
    const msg = messages[0];
    if (!msg) return null;
    return (
      <Container>
        {msg.body ? (
          <div dangerouslySetInnerHTML={{ __html: msg.body }} />
        ) : (
          <Box color="text-body-secondary">{msg.snippet || 'No body content available.'}</Box>
        )}
      </Container>
    );
  }

  function toggleExpanded(id: string) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <SpaceBetween size="s">
      <Box color="text-body-secondary" fontSize="body-s">
        <Icon name="contact" /> {messages.length} messages in this thread
      </Box>
      {messages.map((msg) => (
        <ExpandableSection
          key={msg.messageId}
          expanded={expandedIds.has(msg.messageId)}
          onChange={() => toggleExpanded(msg.messageId)}
          headerText={
            <span style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <span
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  backgroundColor: msg.messageId === activeMessageId ? '#0F1B2D' : '#6b7280',
                  color: 'white',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  fontSize: '12px',
                  flexShrink: 0,
                }}
              >
                {msg.from.charAt(0).toUpperCase()}
              </span>
              <span style={{ fontWeight: msg.readStatus !== 'read' ? 'bold' : 'normal' }}>
                {msg.from.split('@')[0]}
              </span>
              <span style={{ color: '#6b7280', fontSize: '12px', marginLeft: 'auto' }}>
                {formatRelativeTime(msg.receivedAt)}
              </span>
            </span>
          }
        >
          <div style={{ padding: '8px 0' }}>
            {msg.body ? (
              <div dangerouslySetInnerHTML={{ __html: msg.body }} />
            ) : (
              <Box color="text-body-secondary">{msg.snippet || 'No body content.'}</Box>
            )}
          </div>
        </ExpandableSection>
      ))}
    </SpaceBetween>
  );
}
